/** Beat detail: real preview playback (length follows the clip), spec panel,
 *  purchase → download screen. */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../api';
import { usePreviewPlayer } from '../PreviewPlayer';
import { BeatDetail, CheckoutResult, Currency, fmtCents, fmtPoints, mmss } from '../types';
import {
  BLUE, Equalizer, GhostBtn, HeartIcon, NewBadge, PageShell, SpecRow, Waveform,
  cardPanel, fmtCount, s, seedFromId,
} from '../ui';
import { PurchaseModal } from '../components/PurchaseModal';
import { DownloadPanel } from '../components/DownloadPanel';
import { trackCheckoutStarted, trackPurchaseCompleted } from '../analytics';

const BeatDetailPage: React.FC = () => {
  const { beatId } = useParams<{ beatId: string }>();
  const navigate = useNavigate();
  const player = usePreviewPlayer();
  const [beat, setBeat] = useState<BeatDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [currency, setCurrency] = useState<Currency | null>(null);
  const [pointsBalance, setPointsBalance] = useState<number | null>(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [purchased, setPurchased] = useState<CheckoutResult | null>(null);

  useEffect(() => {
    if (!beatId) return;
    let cancelled = false;
    setNotFound(false);
    setPurchased(null);
    setShowPurchase(false);
    api.getBeat(beatId)
      .then((next) => { if (!cancelled) setBeat(next); })
      .catch(() => { if (!cancelled) setNotFound(true); });
    api.getStats().then((st) => { if (!cancelled) setPointsBalance(st.points_balance); }).catch(() => {});
    return () => { cancelled = true; };
  }, [beatId]);

  // When the shop queue skips tracks (mini player or auto-advance), switch this page.
  useEffect(() => {
    if (!beatId || !player.track) return;
    if (player.track.id === beatId) return;
    if (!player.queue.some((t) => t.id === player.track!.id)) return;
    navigate(`/store/beat-shop/beats/${player.track.id}`, { replace: true });
  }, [player.track, player.queue, beatId, navigate]);

  // Resume after Stripe 3DS / wallet redirect: ?beatshop_order=<uuid>&redirect_status=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('beatshop_order');
    if (!orderId) return;

    let cancelled = false;
    const redirectStatus = params.get('redirect_status');

    const clearParams = () => {
      const url = new URL(window.location.href);
      ['beatshop_order', 'payment_intent', 'payment_intent_client_secret', 'redirect_status'].forEach((k) => {
        url.searchParams.delete(k);
      });
      window.history.replaceState({}, '', url.toString());
    };

    (async () => {
      if (redirectStatus === 'failed' || redirectStatus === 'canceled') {
        clearParams();
        return;
      }
      for (let i = 0; i < 30 && !cancelled; i++) {
        try {
          const order = await api.getOrder(orderId);
          if (cancelled) return;
          if (order.status === 'paid') {
            clearParams();
            setPurchased({
              orderId,
              status: 'paid',
              entitlementId: order.entitlement_id,
            });
            if (beatId) trackPurchaseCompleted(beatId, 'cash');
            return;
          }
          if (order.status === 'failed' || order.status === 'expired' || order.status === 'reversed') {
            clearParams();
            return;
          }
        } catch {
          // keep polling briefly
        }
        await new Promise((r) => setTimeout(r, 700));
      }
      clearParams();
    })();

    return () => { cancelled = true; };
  }, [beatId]);

  const switching = !!(beat && beatId && beat.id !== beatId);
  const isThisTrack = !!(beat && player.isCurrent(beat.id));
  const playing = isThisTrack && player.playing;
  const t = isThisTrack ? player.currentTime : 0;
  const frac = isThisTrack ? player.progress : 0;

  const togglePlay = () => {
    if (!beat?.preview_url) return;
    const payload = {
      id: beat.id,
      name: beat.name,
      preview_url: beat.preview_url,
      bpm: beat.bpm,
      musical_key: beat.musical_key,
      seller_name: beat.seller_name,
      is_favorited: beat.is_favorited,
    };
    if (player.isPlayingId(beat.id)) player.pause();
    else player.play(payload);
  };

  const onScrub = (nextFrac: number) => {
    if (!beat?.preview_url) return;
    if (!player.isCurrent(beat.id)) {
      player.play({
        id: beat.id,
        name: beat.name,
        preview_url: beat.preview_url,
        bpm: beat.bpm,
        musical_key: beat.musical_key,
        seller_name: beat.seller_name,
        is_favorited: beat.is_favorited,
      });
    }
    player.seek(nextFrac);
  };

  const toggleFav = async () => {
    if (!beat) return;
    const next = !beat.is_favorited;
    setBeat({ ...beat, is_favorited: next, favorite_count: beat.favorite_count + (next ? 1 : -1) });
    player.setFavorited(beat.id, next);
    try { await api.setFavorite(beat.id, next); } catch { /* optimistic */ }
  };

  if (notFound) {
    return (
      <PageShell>
        <div style={s('text-align:center;padding:80px 0;color:var(--bs-text-muted)')}>
          This beat is no longer available.
          <div style={s('margin-top:18px')}><GhostBtn onClick={() => navigate('/store/beat-shop')}>Back to Beat Shop</GhostBtn></div>
        </div>
      </PageShell>
    );
  }
  if (!beat) return <PageShell><div style={s('color:var(--bs-text-muted);padding:40px 0')}>Loading…</div></PageShell>;

  // post-purchase success screen (prototype's Download screen)
  if (purchased) {
    return (
      <PageShell>
        <DownloadPanel beat={beat} result={purchased} onBack={() => navigate('/store/beat-shop')} />
      </PageShell>
    );
  }

  // playback length follows the clip itself (see PreviewPlayer), so a 45s legacy
  // preview, a 60s generated one and a longer seller clip each read correctly
  const previewSecs = Math.round(player.limit);
  const ended = t >= player.limit;
  const previewNote = !beat.preview_url ? 'Preview unavailable'
    : ended ? 'Preview ended — buy to unlock the full beat & stems'
      : playing ? `Playing ${previewSecs}-second preview…` : `Tap play for a ${previewSecs}-second preview`;
  const priceText = fmtCents(beat.price_cents);
  const sold = beat.status !== 'listed';

  const queueIndex = player.queue.findIndex((t) => t.id === beatId);
  const inShopQueue = queueIndex >= 0 && player.queue.length > 1;
  const canPrev = queueIndex > 0;
  const canNext = queueIndex >= 0 && queueIndex < player.queue.length - 1;

  const goQueueBeat = (dir: -1 | 1) => {
    const target = player.queue[queueIndex + dir];
    if (!target) return;
    if (target.preview_url) player.play(target);
    navigate(`/store/beat-shop/beats/${target.id}`, { replace: true });
  };

  const startPurchase = (next: Currency) => {
    if (next === 'points') return; // coming soon
    setCurrency(next);
    localStorage.setItem('beatshop-currency', next);
    setShowPurchase(true);
    trackCheckoutStarted(beat.id, next);
  };

  const buyBtnBase = 'width:100%;padding:14px;border-radius:11px;font-weight:600;font-size:14px;cursor:pointer;transition:all .12s;border:none;';
  const buyBtnPrimary = `${buyBtnBase}background:var(--bs-btn-bg);color:var(--bs-btn-text);`;
  const buyBtnDisabled = `${buyBtnBase}background:var(--bs-disabled);color:var(--bs-text-faint);cursor:not-allowed;border:1px solid var(--bs-border);`;

  return (
    <PageShell>
      <div style={s('animation:bs-rise .25s ease both')}>
        <button onClick={() => navigate('/store/beat-shop')} style={s('display:inline-flex;align-items:center;gap:7px;background:none;border:none;color:var(--bs-text-muted);font-weight:500;font-size:14px;cursor:pointer;padding:0;margin-bottom:22px')}>‹ Back to Beat Shop</button>
        <div style={s('display:flex;gap:22px;align-items:flex-start;flex-wrap:wrap')}>
          <section style={s('flex:1.5;min-width:min(100%,340px);max-width:100%;' + cardPanel + 'padding:26px')}>
            <div style={s('display:flex;align-items:flex-start;justify-content:space-between;gap:12px')}>
              <div style={s('min-width:0;flex:1')}>
                <h2 className="bs-beat-title">{beat.name}</h2>
                <div className="bs-beat-title-actions">
                  {beat.is_new && <NewBadge />}
                  <button onClick={toggleFav} title="Save to favorites" style={s('display:flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:9px;border:1px solid var(--bs-border-2);background:transparent;cursor:pointer;flex-shrink:0')}>
                    <HeartIcon filled={beat.is_favorited} size={18} stroke="var(--bs-text-mid)" />
                  </button>
                </div>
                <div className="bs-beat-meta" style={s('margin-top:8px;font-size:13.5px;color:var(--bs-text-muted);line-height:1.45')}>
                  {beat.bpm} BPM · {beat.musical_key} · Produced by {beat.seller_name || 'Unknown'} · <span style={s('color:var(--bs-text-body)')}>{fmtCount(beat.favorite_count)}</span> favorited
                </div>
              </div>
              {playing && <div style={s('flex-shrink:0;padding-top:4px')}><Equalizer /></div>}
            </div>

            <div style={s('position:relative;margin:26px 0 0;width:100%;max-width:100%;min-width:0')}>
            <div style={s('margin:0 0 14px;width:100%;max-width:100%;min-width:0;overflow:hidden')}>
              <Waveform seed={seedFromId(beat.id) * 131 + 7} count={88} height={92} progress={frac} onScrub={!switching && beat.preview_url ? onScrub : undefined} />
            </div>
            <div style={s('display:flex;align-items:center;justify-content:space-between;font-size:12px;color:var(--bs-text-muted);margin-bottom:22px;font-variant-numeric:tabular-nums')}>
              <span>{mmss(t)}</span>
              <span style={s('color:#2F6BFF;font-weight:500')}>Preview · {previewSecs}s clip</span>
              <span>-{mmss(Math.max(0, player.limit - t))}</span>
            </div>

            <div style={s('display:flex;align-items:center;gap:12px')}>
              {inShopQueue && (
                <button
                  type="button"
                  onClick={() => goQueueBeat(-1)}
                  disabled={switching || !canPrev}
                  aria-label="Previous beat"
                  style={s(
                    'width:40px;height:40px;border-radius:50%;border:1px solid var(--bs-border-2);display:flex;align-items:center;justify-content:center;flex-shrink:0;' +
                    (!switching && canPrev
                      ? 'background:transparent;color:var(--bs-text-body);cursor:pointer'
                      : 'background:transparent;color:var(--bs-text-faint);opacity:0.35;cursor:not-allowed')
                  )}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M3.5 2.5v11h1.6V9.2L12.5 13.5V2.5L5.1 6.8V2.5H3.5z" /></svg>
                </button>
              )}
              <button type="button" onClick={togglePlay} disabled={!beat.preview_url} style={s('width:54px;height:54px;border-radius:50%;background:var(--bs-btn-bg);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0')}>
                {playing ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="var(--bs-btn-text)"><rect x="3" y="2" width="3.5" height="12" rx="1" /><rect x="9.5" y="2" width="3.5" height="12" rx="1" /></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="var(--bs-btn-text)"><path d="M4 2.5v11l9-5.5z" /></svg>
                )}
              </button>
              {inShopQueue && (
                <button
                  type="button"
                  onClick={() => goQueueBeat(1)}
                  disabled={switching || !canNext}
                  aria-label="Next beat"
                  style={s(
                    'width:40px;height:40px;border-radius:50%;border:1px solid var(--bs-border-2);display:flex;align-items:center;justify-content:center;flex-shrink:0;' +
                    (!switching && canNext
                      ? 'background:transparent;color:var(--bs-text-body);cursor:pointer'
                      : 'background:transparent;color:var(--bs-text-faint);opacity:0.35;cursor:not-allowed')
                  )}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M12.5 2.5v11h-1.6V9.2L3.5 13.5V2.5l7.4 4.3V2.5h1.6z" /></svg>
                </button>
              )}
              <div style={s('flex:1;min-width:0')}>
                <div style={s('height:6px;background:var(--bs-wave-off);border-radius:4px;overflow:hidden')}>
                  <div style={{ ...s('height:100%;background:#2F6BFF;border-radius:4px'), width: `${(frac * 100).toFixed(1)}%` }} />
                </div>
                <div style={{ ...s('margin-top:9px;font-size:12.5px;font-weight:500'), color: ended ? 'var(--bs-blue-soft)' : 'var(--bs-text-muted)' }}>{previewNote}</div>
              </div>
            </div>
            {switching && (
              <div
                aria-busy="true"
                aria-label="Loading beat"
                style={s('position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb,var(--bs-surface) 78%,transparent);border-radius:12px;z-index:2')}
              >
                <div
                  style={s('width:28px;height:28px;border:3px solid var(--bs-border-2);border-top-color:#2F6BFF;border-radius:50%;animation:bs-spin .7s linear infinite')}
                />
              </div>
            )}
            </div>
          </section>

          <aside style={s('flex:1;min-width:min(100%,300px);display:flex;flex-direction:column;gap:16px')}>
            <div style={s(cardPanel + 'padding:22px')}>
              <div style={s('display:flex;align-items:baseline;gap:8px')}>
                <span style={s('font-weight:700;font-size:30px;letter-spacing:-0.02em;color:var(--bs-text)')}>{priceText}</span>
              </div>
              <div style={s('margin-top:5px;font-size:13px;color:var(--bs-text-muted)')}>Exclusive license + 5% of the record, anywhere it's distributed.</div>
              {sold ? (
                <div style={s('margin-top:18px;padding:13px;background:var(--bs-hover);border:1px solid var(--bs-border-2);border-radius:11px;text-align:center;color:var(--bs-text-muted);font-weight:600;font-size:14px')}>
                  {beat.status === 'reserved' ? 'Reserved — payment in progress' : 'No longer available'}
                </div>
              ) : (
                <div style={s('display:flex;flex-direction:column;gap:9px;margin-top:18px')}>
                  <button
                    type="button"
                    onClick={() => startPurchase('cash')}
                    style={s(buyBtnPrimary)}
                  >
                    Buy for {fmtCents(beat.price_cents)}
                  </button>
                  <button
                    type="button"
                    disabled
                    title="Nova Points coming soon"
                    style={s(buyBtnDisabled)}
                  >
                    Buy for {fmtPoints(beat.price_points)} · Coming soon
                  </button>
                </div>
              )}
            </div>

            <div style={s(cardPanel + 'padding:4px 22px 6px')}>
              <SpecRow label="Tempo" value={`${beat.bpm} BPM`} />
              <SpecRow label="Key" value={beat.musical_key} />
              <SpecRow label="Length" value={beat.duration_seconds ? mmss(beat.duration_seconds) : '—'} />
              <div style={s('display:flex;align-items:flex-start;justify-content:space-between;gap:14px;padding:13px 0;border-bottom:1px solid var(--bs-line)')}>
                <span style={s('color:var(--bs-text-muted);font-size:13px')}>Genres</span>
                <span style={s('font-weight:500;font-size:12.5px;color:var(--bs-text-hi);text-align:right')}>{beat.genres.join(', ')}</span>
              </div>
              <div style={s('display:flex;align-items:center;justify-content:space-between;padding:13px 0')}>
                <span style={s('color:var(--bs-text-muted);font-size:13px')}>License</span>
                <span style={s('font-weight:500;font-size:12.5px;color:#2F6BFF')}>Exclusive · master incl.</span>
              </div>
            </div>

            <div style={s('background:rgba(47,107,255,0.05);border:1px solid rgba(47,107,255,0.18);border-radius:16px;padding:16px 18px')}>
              <div style={s('font-size:11px;font-weight:600;letter-spacing:0.12em;color:var(--bs-blue-soft);margin-bottom:11px')}>WHAT YOU GET</div>
              <div style={s('display:flex;flex-direction:column;gap:9px;font-size:13px;font-weight:500;color:var(--bs-text-body)')}>
                {['WAV bounce', `Stems (${beat.files.filter((f) => f.kind === 'stem').length})`, 'Tempo + Key listed', "Removed from the public library the instant it's yours"].map((txt) => (
                  <div key={txt} style={s('display:flex;gap:9px;align-items:center')}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 7.5l3 3 6-7" /></svg>{txt}
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {showPurchase && currency && (
        <PurchaseModal
          beat={beat}
          currency={currency}
          pointsBalance={pointsBalance}
          onClose={() => { setShowPurchase(false); setCurrency(null); }}
          onPurchased={(result) => {
            setShowPurchase(false);
            setPurchased(result);
            trackPurchaseCompleted(beat.id, currency);
            setCurrency(null);
          }}
        />
      )}
    </PageShell>
  );
};

export default BeatDetailPage;
